import React, { useEffect, useState } from 'react';
import { Field } from 'formik';

import {
  InputUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  FormLabelUI,
  FormErrorUI,
  CheckboxUI,
  AddButtonUi,
} from '../Interface';
import companyService from '../../service/company.service';
import { generateAutocompleteItems } from '../../utils/helper';
import projectTypeService from '../../service/projectType.service';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import projectService from '../../service/project.service';
import useConfig from '../../context/ConfigContext';
import dayjs from 'dayjs';
import { Box } from '@mui/material';

export const ProjectFields = ({
  viewField,
  selectedValues,
  values,
  validateField,
  setFieldValue,
  errors,
  params,
  autoCompleteOptions,
  setProjectStartDate,
  companyContactsState,
  selectedCompanyState,
  autocompleteData,
  addField,
  setCompanyDialogOpen,
}) => {
  const { selectedCompany, setSelectedCompany } = selectedCompanyState;
  const { setCompanyContacts, companyContacts } = companyContactsState;

  const { config, getConfig } = useConfig();

  const [projectTypeStatus, setProjectTypeStatus] = useState([]);

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, []);

  useEffect(() => {
    if (!autocompleteData.companyLocation || !autocompleteData.projectType || !config.generateProjWorkingNumber) return;
    if (!viewField && !addField) {
      const location =
        autocompleteData.companyLocation.find((item) => item.id === values.companyLocationId) ??
        autocompleteData.companyLocation[0];
      const projectType =
        autocompleteData.projectType.find((item) => item.id === values.projectTypeId) ??
        autocompleteData.projectType[0];

      const nextOrderNumber = async () => {
        const orderNumber = await projectService.getNextOrderNumber({
          locationPrefix: location.prefix,
          projectTypePrefix: projectType.prefix,
          year: dayjs(values.projectStartDate).format('YY'),
        });
        setFieldValue('orderNumber', orderNumber);
      };

      nextOrderNumber();
    }
  }, [
    values.projectTypeId,
    values.companyLocationId,
    values.projectStartDate,
    autocompleteData.projectType,
    autocompleteData.companyLocation,
  ]);

  const getCompanyData = async (companyId) => {
    const selCom = await companyService.getCompanyById(companyId);
    setSelectedCompany(selCom);
  };

  const getProjectTypeStatus = async (projectTypeId) => {
    const status = await projectTypeService.getProjectTypeStatus(projectTypeId);

    const filteredStatus = status.filter((item) => !item.deletedBy);

    setProjectTypeStatus(generateAutocompleteItems(filteredStatus, 'name', 'id'));
  };

  useEffect(() => {
    if (values.projectTypeId) {
      getProjectTypeStatus(values.projectTypeId);
    } else {
      setProjectTypeStatus([]);
      setFieldValue('projectTypeStatusId', null);
    }
  }, [values.projectTypeId]);

  useEffect(() => {
    if (projectTypeStatus.length === 0) return;

    if (!selectedValues.projectId && !values.projectTypeStatusId) {
      const findedProjectType = autocompleteData.projectType.find((type) => type.id === values.projectTypeId);
      setFieldValue('projectTypeStatusId', findedProjectType?.defaultStatusId ?? null);
    }
  }, [projectTypeStatus]);

  useEffect(() => {
    if (values.companyId) {
      setCompanyContacts([]);
      getCompanyData(values.companyId);
    } else {
      setSelectedCompany(null);
      setCompanyContacts([]);
    }
  }, [values.companyId]);

  useEffect(() => {
    if (!selectedCompany) {
      setFieldValue('companyContactId', null);
      return;
    }

    setCompanyContacts(generateAutocompleteItems(selectedCompany.companyContact, 'name', 'id'));

    if (params.HIDE_PROJECT_COMPANYCONTACT_FIELD === 'Y') return;

    if (!selectedValues.projectId) {
      const defContact = selectedCompany.companyContact.find((cc) => cc.default);
      setFieldValue('companyContactId', defContact?.id ?? selectedCompany?.companyContact[0]?.id ?? null);
    }
  }, [selectedCompany, params.HIDE_PROJECT_COMPANYCONTACT_FIELD]);

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-3 md:mx-4 mb-5">
      <div className={`${params.PROJECT_COMPANY_SHORTNAME_FIELD === 'Y' ? 'md:col-span-2' : 'col-span-4'}`}>
        <Field
          type="text"
          name="projectName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Projekt neve" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectName} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectName !== '' }}
        />
      </div>
      {params.PROJECT_COMPANY_SHORTNAME_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-2">
          <Field
            type="text"
            name="companyShortName"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Cég rövid neve" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyShortName} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyShortName !== '' }}
          />
        </div>
      )}
      <div className={selectedValues.generatedName ? 'col-span-4' : 'hidden'}>
        <Field
          type="text"
          name="generatedName"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Összefűzött projekt név" />}
          variant="standard"
          disabled
          InputLabelProps={{ shrink: values.generatedName !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="text"
          name="orderNumber"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text={getProjectOrderNumberName(params)} />}
          variant="standard"
          helperText={<FormErrorUI message={errors.orderNumber} />}
          disabled={viewField || config?.generateProjWorkingNumber}
          InputLabelProps={{ shrink: values.orderNumber !== '' }}
        />
      </div>
      <div className="col-span-2 md:col-span-1">
        <Field
          type="date"
          name="projectStartDate"
          component={DatePickerUI}
          value={values.projectStartDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectStartDate} />}
          onChange={(date) => {
            setFieldValue('projectStartDate', date).then(() => {
              validateField('projectStartDate');
            });
            setProjectStartDate(date);
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectStartDate !== '' }}
        />
      </div>
      <div className="col-span-2 md:col-span-1">
        <Field
          type="date"
          name="projectEndDate"
          component={DatePickerUI}
          value={values.projectEndDate}
          fullWidth
          label={<FormLabelUI text="Befejező dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectEndDate} />}
          onChange={(date) => {
            setFieldValue('projectEndDate', date).then(() => {
              validateField('projectEndDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectEndDate !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <div className="flex items-center">
          <Field
            type="autocomplete"
            name="companyId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Cég" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyId} />}
            onChange={(_e, newVal) => {
              setFieldValue('companyId', newVal?.value ?? null).then(() => {
                validateField('companyId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyId !== '' }}
            selectedValue={values.companyId}
            selectedLabelValue={
              selectedValues?.company?.name || autocompleteData?.company?.find((cm) => cm.id === values.companyId)?.name
            }
            dataset={autoCompleteOptions.company}
          />
          <AddButtonUi onClick={() => setCompanyDialogOpen(true)} disabled={viewField} />
        </div>
      </div>
      {params.PROJECT_SHOW_PROJECT_TYPE_STATUS !== 'Y' && (
        <div className="col-span-4 md:col-span-2">
          <Field
            type="autocomplete"
            name="statusId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Státusz" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.statusId} />}
            onChange={(_e, newVal) => {
              setFieldValue('statusId', newVal?.value ?? null).then(() => {
                validateField('statusId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.statusId !== '' }}
            selectedValue={values.statusId}
            selectedLabelValue={selectedValues?.status?.itemName}
            dataset={autoCompleteOptions.status}
          />
        </div>
      )}
      {params.HIDE_PROJECT_COMPANYCONTACT_FIELD !== 'Y' && (
        <>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="companyContactId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.companyContactId} />}
              onChange={(_e, newVal) => {
                setFieldValue('companyContactId', newVal?.value ?? null).then(() => {
                  validateField('companyContactId');
                });
              }}
              disabled={viewField}
              InputLabelProps={{ shrink: values.companyContactId !== '' }}
              selectedValue={companyContacts.length > 0 ? values.companyContactId : null}
              selectedLabelValue={
                companyContacts.length > 0
                  ? companyContacts.find((cc) => cc.value === values.companyContactId)?.label
                  : ''
              }
              dataset={companyContacts}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <InputUI
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó telefonszáma" />}
              variant="standard"
              value={
                selectedCompany?.companyContact?.find((cc) => cc.id === values.companyContactId)?.phoneNumber ?? ''
              }
              disabled
            />
          </div>
          <div className="col-span-4 md:col-span-2">
            <InputUI
              fullWidth
              label={<FormLabelUI text="Kapcsolattartó email címe" />}
              variant="standard"
              value={selectedCompany?.companyContact?.find((cc) => cc.id === values.companyContactId)?.email ?? ''}
              disabled
            />
          </div>
        </>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="projectTypeId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt típus" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectTypeId} />}
          onChange={(_e, newVal) => {
            setFieldValue('projectTypeId', newVal?.value ?? null).then(() => {
              validateField('projectTypeId');
            });
            setFieldValue('projectTypeStatusId', null);
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectTypeId !== '' }}
          selectedValue={values.projectTypeId}
          selectedLabelValue={selectedValues?.projectType?.name}
          dataset={autoCompleteOptions.projectType}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="classificationId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Besorolás" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.classificationId} />}
          onChange={(_e, newVal) => {
            setFieldValue('classificationId', newVal?.value ?? null).then(() => {
              validateField('classificationId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.classificationId !== '' }}
          selectedValue={values.classificationId}
          selectedLabelValue={selectedValues?.classification?.name}
          dataset={autoCompleteOptions.classification}
        />
      </div>
      {params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="projectTypeStatusId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Típus státusz" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.projectTypeStatusId} />}
            onChange={(_e, newVal) => {
              setFieldValue('projectTypeStatusId', newVal?.value ?? null).then(() => {
                validateField('projectTypeStatusId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.projectTypeStatusId !== '' }}
            selectedValue={projectTypeStatus.length > 0 && values.projectTypeStatusId}
            selectedLabelValue={
              projectTypeStatus.length > 0
                ? projectTypeStatus.find((cc) => cc.value === values.projectTypeStatusId)?.label
                : ''
            }
            dataset={projectTypeStatus}
          />
        </div>
      )}
      {params.PROVINCE_FIELD_AND_TAB === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="provinceId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Tartomány" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.provinceId} />}
            onChange={(_e, newVal) => {
              setFieldValue('provinceId', newVal?.value ?? null).then(() => {
                validateField('provinceId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.provinceId !== '' }}
            selectedValue={values.provinceId}
            selectedLabelValue={selectedValues?.province?.name}
            dataset={autoCompleteOptions.province}
          />
        </div>
      )}
      {params.PROJECT_COSTCENTER_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="costCenterCode"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Költséghely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.costCenterCode} />}
            onChange={(_e, newVal) => {
              setFieldValue('costCenterCode', newVal?.value ?? null).then(() => {
                validateField('costCenterCode');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.costCenterCode !== '' }}
            selectedValue={values.costCenterCode}
            selectedLabelValue={selectedValues?.costCenter?.name}
            dataset={autoCompleteOptions.costCenter}
          />
        </div>
      )}
      {params.PROJECT_COMPANY_LOCATION_FIELD === 'Y' && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="companyLocationId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Telephely" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyLocationId} />}
            onChange={(_e, newVal) => {
              setFieldValue('companyLocationId', newVal?.value ?? null).then(() => {
                validateField('companyLocationId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyLocationId !== '' }}
            selectedValue={values.companyLocationId}
            selectedLabelValue={selectedValues?.companyLocation?.name}
            dataset={autoCompleteOptions.companyLocation}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          type="autocomplete"
          name="userId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Rögzítette" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.userId} />}
          disabled
          InputLabelProps={{ shrink: values.userId !== '' }}
          selectedValue={values.userId}
          selectedLabelValue={selectedValues?.user?.nickName}
          dataset={autoCompleteOptions?.user}
        />
      </div>
      <div className="md:col-span-1 col-span-4 pt-2">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
        />
      </div>
    </div>
  );
};
